"use client";

import React, { createContext, useContext, useCallback } from "react";
import {
  useCredits,
  type CreditsData,
  type CreditsResponse,
} from "@/hooks/useCredits";
import { logger } from "@/utils/logger";

interface CreditsContextValue {
  // Data
  balance: number | null;
  transactions: CreditsData["transactions"];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  sessionReady: boolean;

  // Actions
  refresh: (reason?: string) => Promise<CreditsResponse | undefined>;
  getCurrentBalance: () => number | null;
  hasSufficientCredits: (required?: number) => boolean | null;

  // Legacy support (for gradual migration)
  invalidateCredits: () => Promise<CreditsResponse | undefined>;
  refetch: ReturnType<typeof useCredits>["refetch"];
}

const CreditsContext = createContext<CreditsContextValue | null>(null);

export function CreditsProvider({
  children,
  initialData,
}: {
  children: React.ReactNode;
  initialData?: CreditsResponse;
}) {
  const credits = useCredits(initialData);
  const { invalidateCredits } = credits;

  // Centralized refresh function with logging
  const refresh = useCallback(
    async (reason = "manual") => {
      logger.debug("Refreshing credits via context", {
        component: "CreditsProvider",
        reason,
      });
      return await invalidateCredits();
    },
    [invalidateCredits]
  );

  const value: CreditsContextValue = {
    balance: credits.balance,
    transactions: credits.transactions,
    isLoading: credits.isLoading,
    isFetching: credits.isFetching,
    error: credits.error,
    sessionReady: credits.sessionReady,
    refresh,
    getCurrentBalance: credits.getCurrentBalance,
    hasSufficientCredits: credits.hasSufficientCredits,
    // Legacy support
    invalidateCredits: credits.invalidateCredits,
    refetch: credits.refetch,
  };

  return (
    <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>
  );
}

export function useCreditsContext() {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error("useCreditsContext must be used within CreditsProvider");
  }
  return context;
}

// Safe version that falls back to direct hook if not in provider
export function useSafeCreditsContext(initialData?: CreditsResponse) {
  const context = useContext(CreditsContext);
  // Always call hooks unconditionally - React Hooks rules
  const fallbackCredits = useCredits(initialData);

  if (context) {
    return context;
  }
  // Fallback to direct hook for gradual migration
  return fallbackCredits;
}
